import type { DiagnosticData } from '@/services/noteGoApi/noteGoApiSchemas';
import { errorReporter } from '@/services/errorReportingService';

const EXTENSION_CHECK_TIMEOUT_MS = 700;
const EXTENSION_DIAGNOSTICS_TIMEOUT_MS = 2500;

/**
 * Check if the Chrome extension is installed using a two-phase approach:
 * 1. Try fetching a known extension resource (fast, works when icon is web-accessible)
 * 2. Fall back to postMessage ping (reliable across Chrome MV3 environments)
 *
 * Returns false on timeout or if the extension ID is not configured.
 * @param timeoutMs Maximum time to wait for the extension response (default: 700ms)
 */
export async function checkExtensionInstalled(
  timeoutMs: number = EXTENSION_CHECK_TIMEOUT_MS,
  overrideExtensionId?: string,
): Promise<boolean> {
  const extensionId = overrideExtensionId || import.meta.env.VITE_CHROME_EXTENSION_ID;

  if (!extensionId || extensionId === 'invalid' || extensionId === 'undefined') {
    return false;
  }

  // Phase 1: Try fetch-based detection (fast when icon is web-accessible)
  const fetchResult = await detectViaFetch(extensionId, timeoutMs);
  if (fetchResult) {
    return true;
  }

  // Phase 2: Fall back to postMessage-based detection
  return detectViaPostMessage(timeoutMs);
}

/**
 * Detect extension by fetching a known resource. Returns true if accessible.
 */
async function detectViaFetch(extensionId: string, timeoutMs: number): Promise<boolean> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    await fetch(`chrome-extension://${extensionId}/icons/icon-32.png`, {
      signal: controller.signal,
      mode: 'no-cors',
    });
    return true;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

/**
 * Detect extension by sending a PING_EXTENSION postMessage and waiting for
 * any response from the extension (EXTENSION_PONG or EXTENSION_AUTH_STATE).
 * Matches the pattern used by waitForExtensionResponse in extensionAuthSync.ts.
 */
function detectViaPostMessage(timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;

    const finish = (detected: boolean) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', handler);
      window.clearTimeout(timeoutId);
      resolve(detected);
    };

    const handler = (event: MessageEvent) => {
      // The extension content script posts on this same window — reject anything
      // from another window (frames, popups) or another origin.
      if (event.source !== window || event.origin !== window.location.origin) return;

      const data = event.data as Record<string, unknown> | null;
      if (!data || typeof data !== 'object') return;

      // Accept messages from extension source or known extension types
      const isExtensionSource = data.source === 'notego-extension';
      const isKnownType = data.type === 'EXTENSION_AUTH_STATE' || data.type === 'EXTENSION_PONG';

      if (isExtensionSource || isKnownType) {
        finish(true);
      }
    };

    window.addEventListener('message', handler);

    const timeoutId = window.setTimeout(() => finish(false), timeoutMs);

    // Send both ping and auth request to maximize chances of getting a response
    window.postMessage({ type: 'PING_EXTENSION', source: 'notego-web' }, window.location.origin);
    window.postMessage({ type: 'REQUEST_EXTENSION_AUTH' }, window.location.origin);
  });
}

/**
 * @deprecated Use checkExtensionInstalled() instead.
 */
export const isExtensionInstalled = checkExtensionInstalled;

/**
 * Runtime check that a value is a plain object (not null, not an array).
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Check if a postMessage event is from the NoteGo Chrome extension.
 * Requires the message to be posted on this same window from the same origin
 * (the extension content script runs in the page), then accepts messages with
 * source 'notego-extension' OR known diagnostic types, matching the validation
 * pattern used in extensionAuthSync.ts waitForExtensionResponse.
 */
function isExtensionDiagnosticMessage(event: MessageEvent, data: Record<string, unknown>): boolean {
  if (event.source !== window || event.origin !== window.location.origin) {
    return false;
  }

  const isExtensionSource = data.source === 'notego-extension';
  const isDiagnosticType = data.type === 'EXTENSION_DIAGNOSTIC_DATA' || data.type === 'EXTENSION_DIAGNOSTIC_ERROR';

  return isExtensionSource || isDiagnosticType;
}

/**
 * Request diagnostic data from the Chrome extension via the postMessage bridge.
 * Resolves to the diagnostic payload on success, or null if the extension does
 * not respond within the timeout, returns an error, or sends invalid data.
 * @param timeoutMs Maximum time to wait for the extension response (default: 2500ms)
 */
export function requestExtensionDiagnosticData(
  timeoutMs: number = EXTENSION_DIAGNOSTICS_TIMEOUT_MS,
): Promise<DiagnosticData | null> {
  return new Promise((resolve) => {
    let settled = false;

    const finish = (payload: DiagnosticData | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', handler);
      window.clearTimeout(timeoutId);
      resolve(payload);
    };

    const handler = (event: MessageEvent) => {
      const data = event.data as Record<string, unknown> | null;
      if (!data || typeof data !== 'object') return;
      if (!isExtensionDiagnosticMessage(event, data)) return;

      if (data.type === 'EXTENSION_DIAGNOSTIC_DATA') {
        if (isPlainObject(data.payload)) {
          finish(data.payload as DiagnosticData);
        } else {
          errorReporter.expected('Extension sent non-object diagnostic payload', undefined, {
            feature: 'extension',
            operation: 'requestExtensionDiagnosticData',
          });
          finish(null);
        }
      }

      if (data.type === 'EXTENSION_DIAGNOSTIC_ERROR') {
        errorReporter.expected('Extension reported diagnostic collection error', undefined, {
          feature: 'extension',
          operation: 'requestExtensionDiagnosticData',
        });
        finish(null);
      }
    };

    window.addEventListener('message', handler);

    const timeoutId = window.setTimeout(() => {
      errorReporter.debug('Extension diagnostic data request timed out', {
        feature: 'extension',
        operation: 'requestExtensionDiagnosticData',
      });
      finish(null);
    }, timeoutMs);

    window.postMessage(
      {
        type: 'REQUEST_EXTENSION_DIAGNOSTIC_DATA',
        source: 'notego-web',
      },
      window.location.origin,
    );
  });
}
